import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CalendarComponent, CalendarComponentOptions, DayConfig } from '@exeevo/ion2-calendar';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { PopoverController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'go-app-calendar',
  templateUrl: './go-app-calendar.html',
  styleUrls: ['./go-app-calendar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoAppCalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() fromDate: Date = null;
  @Input() toDate: Date = null;
  @Input() selectedDate: Date = null;
  @Input() highlightRangeStart: Date = null;
  @Input() highlightRangeEnd: Date = null;

  @ViewChild(CalendarComponent) private calendarRef: CalendarComponent;

  currentDate = new Date();
  calendarOptions: CalendarComponentOptions = {};

  private readonly destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private popoverCtrl: PopoverController,
    private dateTimeFormatsService: DateTimeFormatsService,
  ) {}

  ngOnInit() {
    this.updateCalendarOptions();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateCalendarOptions();
      this.patchCalendarMonthFormat();
    });
  }

  ngAfterViewInit() {
    this.patchCalendarMonthFormat();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private langCodeToIntlLocale(code: string): string {
    if (!code) return 'en';
    return code.replace('_', '-');
  }

  private buildLocalizedWeekdays(): string[] {
    const locale = this.langCodeToIntlLocale(this.translate.currentLang);
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2023-01-01 is a Sunday. Build labels in Sunday-first order (index 0 = Sunday),
    // which is the order @exeevo/ion2-calendar expects; it rotates them internally for weekStart=1.
    const sunday = new Date(2023, 0, 1);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(sunday);
      day.setDate(sunday.getDate() + i);
      return formatter.format(day);
    });
  }

  updateCalendarOptions() {
    this.calendarOptions = {
      from: this.fromDate,
      to: 0,
      pickMode: 'single',
      weekdays: this.buildLocalizedWeekdays(),
      weekStart: 1,
      monthFormat: 'MMMM YYYY',
      monthPickerFormat: [
        this.translate.instant('JAN'), this.translate.instant('FEB'), this.translate.instant('MAR'), this.translate.instant('APR'),
        this.translate.instant('MAY'), this.translate.instant('JUN'), this.translate.instant('JUL'), this.translate.instant('AUG'),
        this.translate.instant('SEP'), this.translate.instant('OCT'), this.translate.instant('NOV'), this.translate.instant('DEC')
      ],
      showMonthPicker: false,
      defaultTitle: ''
    };
    let daysConfig = this._disableDaysOnEndDate(this.fromDate, this.toDate);
    if (this.highlightRangeStart && this.highlightRangeEnd) {
      let current = new Date(this.highlightRangeStart);
      const end = new Date(this.highlightRangeEnd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      while (current <= end) {
        const isToday = current.getFullYear() === today.getFullYear() &&
                        current.getMonth() === today.getMonth() &&
                        current.getDate() === today.getDate();
        daysConfig.push({
          date: new Date(current),
          cssClass: isToday ? 'highlighted-range today-highlight' : 'highlighted-range'
        });
        current.setDate(current.getDate() + 1);
      }
    }
    this.calendarOptions.daysConfig = daysConfig;
  }

  private patchCalendarMonthFormat() {
    if (this.calendarRef) {
      this.calendarRef['_monthFormat'] = (date: number) =>
        this.dateTimeFormatsService.formatDate(new Date(date), 'MMMM YYYY');
    }
  }

  monthChange(_event: any) {
    this.calendarOptions.daysConfig = this._disableDaysOnEndDate(this.fromDate, this.toDate);
  }

  private _disableDaysOnEndDate(startTime: Date, endTime: Date): DayConfig[] {
    let _daysConfig: DayConfig[] = [];
    const formattedStartDateTime = startTime.setSeconds(0, 0);
    const formattedEndDateTime = endTime.setSeconds(0, 0);
    const diffDays = this.dateTimeFormatsService.getDifferenceInCalendarDays(formattedEndDateTime, formattedStartDateTime);
    const diffTime: number = Math.ceil(this.dateTimeFormatsService.getDifferenceInMinutes(new Date(formattedEndDateTime), new Date(formattedStartDateTime)));
    const curStartDateTime = startTime;
    const curYear: number = curStartDateTime.getFullYear();
    const curMonth: number = curStartDateTime.getMonth();
    const curDay: number = curStartDateTime.getDate();
    const disabledMaxDay: number = (diffDays != 0 && diffTime <= 1440) ? curDay + 1 : curDay;
    const disabledMaxDayOnPrevMonth: number = new Date(curYear, curMonth, 0).getDate();
    for (let d = 20; d <= disabledMaxDayOnPrevMonth; d++) {
      _daysConfig.push({
        date: new Date(curYear, curMonth - 1, d),
        disable: true,
      });
    }
    if (curDay > 1) {
      for (let d = 1; d < disabledMaxDay; d++) {
        _daysConfig.push({
          date: new Date(curYear, curMonth, d),
          disable: true,
        });
      }
    }
    return _daysConfig;
  }

  onDateChange(event: any) {
    if (event) {
      const selectedDate = new Date(event);
      this.popoverCtrl.dismiss({ selectedDate: selectedDate });
    }
  }
}
